import { FormattedMessage } from 'react-intl';
import messages from './messages';
import { preferencesColumnTableFilterFn } from '../ManageAffordableWaitlist/utils';
import { YES_NO_OPTIONS } from '../../constants/selectOptions';
import { TEXTING_FILTER_OPTIONS } from '../../constants/texting';
import { textingColumnTableFilterFn } from '../../utils/texting';
import { commaListColumnTableFilterFn } from './utils';

// preferenceOptions holds only the preferences enabled in the property's
// waitlist configuration; with none enabled the column is left out.
export const HEADERS = (
  usePreferencesForRanking = false,
  preferenceOptions = [],
) => [
  {
    id: 'position',
    label: <FormattedMessage {...messages.position} />,
    dataType: 'number',
    sortable: true,
  },
  ...(usePreferencesForRanking
    ? [
        {
          id: 'preferenceWeight',
          label: <FormattedMessage {...messages.preferenceWeight} />,
          dataType: 'number',
          sortable: true,
        },
      ]
    : []),
  {
    id: 'applicationDateTime',
    label: <FormattedMessage {...messages.submissionDateTime} />,
    dataType: 'date',
    sortable: true,
  },
  {
    id: 'affordableProgram',
    label: <FormattedMessage {...messages.affordableProgram} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'applicationCode',
    label: <FormattedMessage {...messages.applicationCode} />,
    dataType: 'number',
    searchable: true,
  },
  {
    id: 'applicants',
    label: <FormattedMessage {...messages.applicants} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'texting',
    label: <FormattedMessage {...messages.texting} />,
    dataType: 'number',
    multiselect: true,
    filterOptions: TEXTING_FILTER_OPTIONS,
  },
  {
    id: 'householdStatus',
    label: <FormattedMessage {...messages.householdStatus} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'status',
    label: <FormattedMessage {...messages.status} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'phoneNumber',
    label: <FormattedMessage {...messages.phoneNumber} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'emailAddress',
    label: <FormattedMessage {...messages.emailAddress} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'desiredMoveInDate',
    label: <FormattedMessage {...messages.desiredMoveInDate} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'applicationReceived',
    label: <FormattedMessage {...messages.applicationReceived} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'applicationRemoved',
    label: <FormattedMessage {...messages.applicationRemoved} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'preferredUnitSize',
    label: <FormattedMessage {...messages.preferredUnitSize} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'householdSize',
    label: <FormattedMessage {...messages.householdSize} />,
    dataType: 'number',
    searchable: true,
  },
  {
    id: 'incomeLevel',
    label: <FormattedMessage {...messages.incomeLevel} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'datesContacted',
    label: <FormattedMessage {...messages.datesContacted} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'moveInDate',
    label: <FormattedMessage {...messages.moveInDate} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'needsUnitAccessible',
    label: <FormattedMessage {...messages.needsUnitAccessible} />,
    dataType: 'boolean',
    multiselect: true,
    filterOptions: YES_NO_OPTIONS,
  },
  {
    id: 'requestingAccommodation',
    label: <FormattedMessage {...messages.requestingAccommodation} />,
    dataType: 'boolean',
    multiselect: true,
    filterOptions: YES_NO_OPTIONS,
  },
  ...(preferenceOptions.length
    ? [
        {
          id: 'preferences',
          label: <FormattedMessage {...messages.preferences} />,
          dataType: 'string',
          multiselect: true,
          filterOptions: preferenceOptions,
        },
      ]
    : []),
  {
    id: 'assignedTo',
    label: <FormattedMessage {...messages.assignedTo} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'actions',
    label: <FormattedMessage {...messages.actions} />,
    dataType: 'string',
  },
];

export const PROPERTY_PATH_MAP = {
  position: { path: 'rank', dataType: 'number' },
  preferenceWeight: { path: 'preferenceWeight', dataType: 'number' },
  applicationDateTime: {
    path: 'affordableWaitlist.applicationDateTime',
    dataType: 'date',
  },
  affordableProgram: {
    path: 'affordableWaitlist.affordableProgram',
    dataType: 'string',
    filterPath: 'affordableWaitlist.affordableProgram',
    filterFn: commaListColumnTableFilterFn,
  },
  applicationCode: {
    path: 'affordableWaitlist.application.applicationCode',
    dataType: 'number',
  },
  applicants: {
    path: 'affordableWaitlist.application.applicants',
    dataType: 'string',
  },
  texting: {
    path: 'texting.TOTAL',
    dataType: 'number',
    filterPath: 'texting',
    filterFn: textingColumnTableFilterFn,
  },
  householdStatus: {
    path: 'affordableWaitlist.application.householdStatus',
    dataType: 'string',
  },
  status: {
    path: 'affordableWaitlist.application.applicationStatus.name',
    dataType: 'string',
  },
  phoneNumber: {
    path: 'affordableWaitlist.application.phoneNumber',
    dataType: 'string',
  },
  emailAddress: {
    path: 'affordableWaitlist.application.emailAddress',
    dataType: 'string',
  },
  desiredMoveInDate: {
    path: 'affordableWaitlist.application.desiredMoveInStartDate',
    dataType: 'date',
  },
  applicationReceived: {
    path: 'affordableWaitlist.application.applicationReceived',
    dataType: 'date',
  },
  applicationRemoved: {
    path: 'affordableWaitlist.application.applicationRemoved',
    dataType: 'date',
  },
  preferredUnitSize: {
    path: 'affordableWaitlist.application.preferredUnitSize',
    dataType: 'string',
    filterPath: 'affordableWaitlist.application.preferredUnitSize',
    filterFn: commaListColumnTableFilterFn,
  },
  householdSize: {
    path: 'affordableWaitlist.application.householdSize',
    dataType: 'number',
  },
  incomeLevel: { path: 'affordableWaitlist.incomeLevel', dataType: 'string' },
  datesContacted: {
    path: 'affordableWaitlist.application.datesContacted',
    dataType: 'date',
  },
  moveInDate: {
    path: 'affordableWaitlist.application.moveInDate',
    dataType: 'date',
  },
  needsUnitAccessible: {
    path: 'affordableWaitlist.application.needsUnitAccessible',
    dataType: 'boolean',
  },
  requestingAccommodation: {
    path: 'affordableWaitlist.requestingAccommodation',
    dataType: 'boolean',
  },
  preferences: {
    path: 'affordableWaitlist.preferences',
    dataType: 'string',
    filterPath: 'affordableWaitlist.preferences',
    filterFn: preferencesColumnTableFilterFn,
  },
  assignedTo: {
    path: 'affordableWaitlist.application.assigneeName',
    dataType: 'string',
  },
};

export const AffordableWaitlistPositionStatuses = {
  WAITING: 'WAITING',
  SELECTED: 'SELECTED',
  HOUSED: 'HOUSED',
  WITHDRAWN: 'WITHDRAWN',
};
